import { Mail, Phone, Plus, Trash2, User } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetBrandingSettingsQuery, useUpdateBrandingSettingsMutation } from "../../../../../app/services/book/saved_tours_branding"


const emptyState = {
  header: { enabled: false, logo_url: "", show_company_name: true, company_name_override: "" },
  manager: { enabled: false, show_name: true, show_phone: true, show_email: true },
  footer: { enabled: false, blocks: [] },
}

const uid = () => Math.random().toString(36).slice(2, 10)

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none shrink-0">
    <span
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0
                  ${checked ? "bg-[#1E9E5B]" : "bg-[#D8DEE6]"}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
                    ${checked ? "translate-x-[18px]" : "translate-x-[3px]"}`}
      />
    </span>
    {label && <span className="text-sm text-[#26303D]">{label}</span>}
  </label>
)

const SectionCard = ({ title, description, enabled, onToggleEnabled, children }) => (
  <div className="border border-[#E7EAEF] rounded-xl p-3 sm:p-4">
    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#1F2733]">{title}</div>
        {description && <div className="text-xs text-[#9AA5B4] mt-0.5">{description}</div>}
      </div>
      <Toggle checked={enabled} onChange={onToggleEnabled} label="" />
    </div>
    {enabled && <div className="pt-2 border-t border-[#F0F2F5] flex flex-col gap-3">{children}</div>}
  </div>
)

const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <div className="text-xs text-[#5A6779] mb-1">{label}</div>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
    />
  </div>
)

const BrandingTab = () => {
  const { t } = useTranslation()
  const { data, isLoading } = useGetBrandingSettingsQuery()
  const [updateBrandingSettings, { isLoading: isSaving }] = useUpdateBrandingSettingsMutation()

  const [state, setState] = useState(emptyState)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data?.settings) {
      setState({
        header: { ...emptyState.header, ...data.settings.header },
        manager: { ...emptyState.manager, ...data.settings.manager },
        footer: { ...emptyState.footer, ...data.settings.footer, blocks: data.settings.footer?.blocks || [] },
      })
    }
  }, [data])

  const patchSection = (section, patch) =>
    setState((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }))

  const addFooterBlock = (type) =>
    patchSection("footer", {
      blocks: [
        ...state.footer.blocks,
        type === "text" ? { id: uid(), type: "text", text: "" } : { id: uid(), type: "link", label: "", url: "" },
      ],
    })

  const updateFooterBlock = (id, patch) =>
    patchSection("footer", {
      blocks: state.footer.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    })

  const removeFooterBlock = (id) =>
    patchSection("footer", { blocks: state.footer.blocks.filter((b) => b.id !== id) })

  const handleSave = async () => {
    await updateBrandingSettings({
      header_enabled: state.header.enabled,
      header_logo_url: state.header.logo_url,
      header_show_company_name: state.header.show_company_name,
      header_company_name_override: state.header.company_name_override,
      manager_enabled: state.manager.enabled,
      manager_show_name: state.manager.show_name,
      manager_show_phone: state.manager.show_phone,
      manager_show_email: state.manager.show_email,
      footer_enabled: state.footer.enabled,
      footer_blocks: state.footer.blocks,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 1800)
  }

  if (isLoading) {
    return <div className="text-sm text-[#9AA5B4] py-10 text-center">{t("common.loading", "Загрузка...")}</div>
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <div className="text-sm text-[#5A6779]">
        {t(
          "saved_tours.branding.description",
          "Настройте блоки, которые будут показываться на странице подборки: хедер, блок менеджера и футер. Настройки применяются ко всем подборкам компании."
        )}
      </div>

      <SectionCard
        title={t("saved_tours.branding.header_title", "Хедер")}
        description={t("saved_tours.branding.header_description", "Логотип и название компании над подборкой")}
        enabled={state.header.enabled}
        onToggleEnabled={(v) => patchSection("header", { enabled: v })}
      >
        <TextField
          label={t("saved_tours.branding.logo_url", "Ссылка на логотип")}
          value={state.header.logo_url}
          onChange={(v) => patchSection("header", { logo_url: v })}
          placeholder="https://..."
        />
        <Toggle
          checked={state.header.show_company_name}
          onChange={(v) => patchSection("header", { show_company_name: v })}
          label={t("saved_tours.branding.show_company_name", "Показывать название компании")}
        />
        {state.header.show_company_name && (
          <TextField
            label={t("saved_tours.branding.company_name_override", "Название компании (необязательно, иначе берётся из профиля)")}
            value={state.header.company_name_override}
            onChange={(v) => patchSection("header", { company_name_override: v })}
          />
        )}
        {(state.header.logo_url || (state.header.show_company_name && state.header.company_name_override)) && (
          <div className="flex flex-wrap items-center gap-3 bg-[#F4F6F9] rounded-lg px-3 sm:px-4 py-3">
            {state.header.logo_url && (
              <img src={state.header.logo_url} alt="" className="h-8 w-auto max-w-[120px] object-contain shrink-0" />
            )}
            {state.header.show_company_name && (
              <span className="text-sm font-semibold text-[#1F2733] break-words min-w-0">
                {state.header.company_name_override || t("saved_tours.branding.company_name_placeholder", "Название компании")}
              </span>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title={t("saved_tours.branding.manager_title", "Блок менеджера")}
        description={t(
          "saved_tours.branding.manager_description",
          "Имя, телефон и почта показываются автоматически по менеджеру, создавшему подборку — здесь выбираете, какие поля видны"
        )}
        enabled={state.manager.enabled}
        onToggleEnabled={(v) => patchSection("manager", { enabled: v })}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-4">
          <Toggle
            checked={state.manager.show_name}
            onChange={(v) => patchSection("manager", { show_name: v })}
            label={t("saved_tours.branding.show_name", "Имя")}
          />
          <Toggle
            checked={state.manager.show_phone}
            onChange={(v) => patchSection("manager", { show_phone: v })}
            label={t("saved_tours.branding.show_phone", "Телефон")}
          />
          <Toggle
            checked={state.manager.show_email}
            onChange={(v) => patchSection("manager", { show_email: v })}
            label={t("saved_tours.branding.show_email", "Почта")}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-1.5 text-sm text-[#26303D] bg-[#F4F6F9] rounded-lg px-3 sm:px-4 py-3">
          {state.manager.show_name && (
            <span className="flex items-center gap-1.5">
              <User size={14} className="text-[#9AA5B4]" /> {t("saved_tours.branding.manager_example_name", "Иван Иванов")}
            </span>
          )}
          {state.manager.show_phone && (
            <span className="flex items-center gap-1.5">
              <Phone size={14} className="text-[#9AA5B4]" /> +373 000 000
            </span>
          )}
          {state.manager.show_email && (
            <span className="flex items-center gap-1.5">
              <Mail size={14} className="text-[#9AA5B4]" /> manager@example.com
            </span>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title={t("saved_tours.branding.footer_title", "Футер")}
        description={t("saved_tours.branding.footer_description", "Произвольные блоки текста или ссылок внизу страницы")}
        enabled={state.footer.enabled}
        onToggleEnabled={(v) => patchSection("footer", { enabled: v })}
      >
        {state.footer.blocks.map((block) => (
          <div key={block.id} className="flex items-start gap-2 bg-[#F4F6F9] rounded-lg p-3">
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              {block.type === "text" ? (
                <textarea
                  value={block.text}
                  onChange={(e) => updateFooterBlock(block.id, { text: e.target.value })}
                  placeholder={t("saved_tours.branding.footer_text_placeholder", "Текст блока")}
                  rows={2}
                  className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-[#9AA5B4]"
                />
              ) : (
                <>
                  <input
                    type="text"
                    value={block.label}
                    onChange={(e) => updateFooterBlock(block.id, { label: e.target.value })}
                    placeholder={t("saved_tours.branding.footer_link_label_placeholder", "Текст ссылки")}
                    className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
                  />
                  <input
                    type="text"
                    value={block.url}
                    onChange={(e) => updateFooterBlock(block.id, { url: e.target.value })}
                    placeholder="https://..."
                    className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
                  />
                </>
              )}
            </div>
            <button
              onClick={() => removeFooterBlock(block.id)}
              className="p-2 rounded-lg text-[#9AA5B4] hover:text-red-500 hover:bg-white transition-colors shrink-0"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => addFooterBlock("text")}
            className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
          >
            <Plus size={13} /> {t("saved_tours.branding.add_text_block", "Текстовый блок")}
          </button>
          <button
            onClick={() => addFooterBlock("link")}
            className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
          >
            <Plus size={13} /> {t("saved_tours.branding.add_link_block", "Ссылка")}
          </button>
        </div>
      </SectionCard>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="text-sm text-white bg-[#26303D] rounded-lg px-4 py-2 disabled:opacity-60 w-full sm:w-auto"
        >
          {isSaving ? t("common.saving", "Сохранение...") : t("common.save", "Сохранить")}
        </button>
        {saved && <span className="text-sm text-[#1E9E5B]">{t("common.saved", "Сохранено")}</span>}
      </div>
    </div>
  )
}

export default BrandingTab
